import { SxProps, styled } from "@mui/material";
import { CSSProperties, ReactElement, useState } from "react";

export enum Sort {
    Asc,
    Desc,
}

export interface ColumnDefinition<T> {
    name: string;
    build: (row: T) => ReactElement;
    buildHead?: () => ReactElement;
    sort: (rowA: T, rowB: T) => number;
    columnStyle?: CSSProperties;
}

function invertSort(sort: Sort | undefined): Sort {
    if (sort === Sort.Asc) {
        return Sort.Desc;
    }

    return Sort.Asc;
}

interface ColumnHeadProps {
    text: string;
    sort?: Sort;
    buildHead?: () => ReactElement;
    onSort: (order: Sort) => void;
}

function ColumnHead({ text, sort, onSort, buildHead }: ColumnHeadProps) {
    return (
        <div className="recipe-table-column-head" onClick={() => onSort(invertSort(sort))}>
            <div style={{ flexGrow: 1 }}>{text}</div>
            {buildHead?.()}
            <div style={{ width: 10 }}>{sort === Sort.Asc ? "↑" : sort === Sort.Desc ? "↓" : undefined}</div>
        </div>
    );
}

interface TrProps {
    sx?: SxProps;
}

const Tr = styled("tr")<TrProps>();

export interface TableSort {
    column: string;
    order: Sort;
}

export interface TableProps<T> {
    columns: ColumnDefinition<T>[];
    rows: T[];
    onRowClick?: (row: T) => void;
    rowSx?: SxProps;
}

export default function Table<T extends { id: number | string }>({ columns, rows, onRowClick, rowSx }: TableProps<T>) {
    const [sort, setSort] = useState<TableSort | undefined>(undefined);
    let sortedRows = rows.slice();
    if (sort) {
        const column = columns.find(c => c.name === sort.column);
        if (column) {
            if (sort.order === Sort.Asc) {
                sortedRows.sort(column.sort);
            } else {
                sortedRows.sort((a, b) => column.sort(b, a));
            }
        }
    }

    return (
        <table style={{ height: "1px", backgroundColor: "white" }}>
            <thead>
                <tr>
                    {columns.map((column, index) => {
                        const onSort = (order: Sort) => {
                            setSort({ column: column.name, order });
                        };

                        return (
                            <th key={index} style={column.columnStyle}>
                                <ColumnHead
                                    key={index}
                                    text={column.name}
                                    sort={sort?.column === column.name ? sort.order : undefined}
                                    onSort={onSort}
                                ></ColumnHead>
                            </th>
                        );
                    })}
                </tr>
            </thead>
            <tbody>
                {sortedRows.map(row => {
                    return (
                        <Tr
                            key={row.id}
                            className="recipe-table-column-row"
                            onClick={onRowClick ? () => onRowClick(row) : undefined}
                            sx={rowSx}
                        >
                            {columns.map(column => (
                                <td key={column.name}>{column.build(row)}</td>
                            ))}
                        </Tr>
                    );
                })}
            </tbody>
        </table>
    );
}
